import React, { useEffect, useRef, useState } from "react";
import ProgressSteps from "../components/ProgressSteps";
import Step1PersonalInfo from "../steps/Step1PersonalInfo";
import Step2VehicleInfo from "../steps/Step2VehicleInfo";
import Step3PaymentInfo from "../steps/Step3PaymentInfo";
import Step4TransferInfo from "../steps/Step4TransferInfo";
import Step5ConditionInfo from "../steps/Step5ConditionInfo";
import Step6DamageInfo from "../steps/Step6DamageInfo";
import Step7Signature from "../steps/Step7Signature";
import { completeSellerContract, getContract } from "../api/contractApi";
import { getStoredProfileSignature } from "../utils/profileSignature";

const INITIAL_SELLER_DATA = {
  firstName: "",
  lastName: "",
  fatherName: "",
  idNumber: "",
  nationalCode: "",
  idIssuePlace: "",
  birthDate: "",
  address: "",
  phone: "",
  mobile: "",
  vehicleName: "",
  vehicleModel: "",
  vehicleColor: "",
  chassisNumber: "",
  engineNumber: "",
  plateNumber: "",
  amountToman: "",
  amountRial: "",
  paymentTerms: "",
  dealDescription: "",
  transferFeeBy: "",
  plateFeeBy: "",
  notaryDate: "",
  notaryNumber: "",
  notaryAddress: "",
  deliveryDate: "",
  deliveryTime: "",
  vehicleCondition: "",
  vehicleMileage: "",
  penaltyDescription: "",
  signature: null,
};

const STEPS = [
  { title: "اطلاعات هویتی", component: Step1PersonalInfo },
  { title: "اطلاعات خودرو", component: Step2VehicleInfo },
  { title: "مبلغ و شرایط", component: Step3PaymentInfo },
  { title: "نقل و انتقال", component: Step4TransferInfo },
  { title: "وضعیت تحویل", component: Step5ConditionInfo },
  { title: "ضرر و زیان", component: Step6DamageInfo },
  { title: "بازبینی و امضا", component: Step7Signature },
];

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-sky border-t-transparent" />
        <p className="text-sm text-gray-500">
          در حال بارگذاری اطلاعات قولنامه...
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-card">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-2xl">
          ⚠️
        </div>
        <h2 className="mb-2 text-lg font-bold text-navy">
          خطا در بارگذاری قولنامه
        </h2>
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    </div>
  );
}

function BuyerInfoBanner({ buyerData }) {
  if (!buyerData) return null;
  return (
    <div className="mb-6 rounded-2xl border border-sky/30 bg-sky/5 p-4">
      <p className="mb-1 text-xs font-bold text-sky-dark">
        اطلاعات خریدار (ثبت‌شده)
      </p>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="text-gray-500">نام: </span>
          <span className="font-medium text-navy">
            {buyerData.name ||
              `${buyerData.firstName || ""} ${buyerData.lastName || ""}`.trim() ||
              "—"}
          </span>
        </span>
        <span>
          <span className="text-gray-500">کد ملی: </span>
          <span className="font-medium text-navy">
            {buyerData.nationalCode || "—"}
          </span>
        </span>
        <span>
          <span className="text-gray-500">موبایل: </span>
          <span className="font-medium text-navy">
            {buyerData.mobile || "—"}
          </span>
        </span>
      </div>
    </div>
  );
}

export default function SellerCompleteWizard({ contractId, onComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(() => ({
    ...INITIAL_SELLER_DATA,
    signature: getStoredProfileSignature(),
  }));
  const [buyerData, setBuyerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const contract = await getContract(contractId);
        if (
          contract.contract.status === "completed" ||
          contract.contract.status === "cancelled"
        ) {
          onComplete(contract.contract);
          return;
        }
        setBuyerData(contract.buyer || null);
      } catch (err) {
        setLoadError(err.message || "اطلاعات قولنامه یافت نشد.");
      } finally {
        setLoading(false);
      }
    })();
  }, [contractId]);

  if (loading) return <LoadingScreen />;
  if (loadError) return <ErrorScreen message={loadError} />;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignatureChange = (dataUrl) => {
    setFormData((prev) => ({ ...prev, signature: dataUrl }));
  };

  const isLastStep = currentStep === STEPS.length;
  const isFirstStep = currentStep === 1;

  const handleFinalSubmit = async () => {
    if (!formData.signature) {
      window.alert("لطفاً پیش از ثبت نهایی، امضای خود را وارد کنید.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {
        seller: {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          fatherName: formData.fatherName,
          idNumber: formData.idNumber,
          nationalCode: formData.nationalCode,
          idIssuePlace: formData.idIssuePlace,
          birthDate: formData.birthDate,
          address: formData.address,
          phone: formData.phone,
          mobile: formData.mobile,
          w: formData.signature,
        },
        vehicle: {
          name: formData.vehicleName,
          model: formData.vehicleModel,
          color: formData.vehicleColor,
          chassisNumber: formData.chassisNumber,
          engineNumber: formData.engineNumber,
          plateNumber: formData.plateNumber,
        },
        deal: {
          amountRial: formData.amountRial,
          amountToman: formData.amountToman,
          paymentTerms: formData.paymentTerms,
          description: formData.dealDescription,
        },
        transfer: {
          transferFeeBy: formData.transferFeeBy,
          plateFeeBy: formData.plateFeeBy,
          notaryDate: formData.notaryDate,
          notaryNumber: formData.notaryNumber,
          notaryAddress: formData.notaryAddress,
        },
        delivery: {
          date: formData.deliveryDate,
          time: formData.deliveryTime,
          condition: formData.vehicleCondition,
          mileage: formData.vehicleMileage,
        },
        penalty: {
          description: formData.penaltyDescription,
        },
      };

      await completeSellerContract(contractId, payload);
      const fullContract = await getContract(contractId);
      onComplete(fullContract.contract);
    } catch (err) {
      setSubmitError(err.message || "خطا در ارتباط با سرور. دوباره تلاش کنید.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    if (isLastStep) {
      handleFinalSubmit();
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const goBack = () => {
    if (!isFirstStep) setCurrentStep((s) => Math.max(s - 1, 1));
  };

  const CurrentStepComponent = STEPS[currentStep - 1].component;

  return (
    <div className="min-h-screen bg-surface px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-lg text-white">
            🚗
          </div>
          <div>
            <h1 className="text-base font-extrabold text-navy sm:text-lg">
              قولنامه هوشمند — فروشنده
            </h1>
            <p className="text-xs text-gray-500">قرارداد ۲۴</p>
          </div>
        </div>

        <BuyerInfoBanner buyerData={buyerData} />

        <div className="mb-6 rounded-2xl bg-white p-4 shadow-card sm:p-5">
          <ProgressSteps steps={STEPS} currentStep={currentStep} />
        </div>

        <form
          ref={formRef}
          onSubmit={(e) => e.preventDefault()}
          className="step-enter rounded-2xl bg-white p-5 shadow-card sm:p-8"
        >
          <CurrentStepComponent
            data={formData}
            onChange={handleChange}
            onSignatureChange={handleSignatureChange}
          />

          {submitError && (
            <p className="mt-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600">
              ⚠️ {submitError}
            </p>
          )}
        </form>

        <div className="mt-6 flex items-center justify-between gap-3">
          {!isFirstStep ? (
            <button
              type="button"
              onClick={goBack}
              disabled={submitting}
              className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-bold text-navy transition hover:border-sky-light disabled:opacity-50"
            >
              مرحله قبل
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="flex items-center gap-2 rounded-xl bg-sky px-8 py-2.5 text-sm font-bold text-white shadow-card transition hover:bg-sky-dark disabled:opacity-60"
          >
            {submitting && (
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            )}
            {submitting
              ? "در حال ثبت..."
              : isLastStep
                ? "ثبت نهایی و تکمیل قولنامه"
                : "مرحله بعد"}
          </button>
        </div>
      </div>
    </div>
  );
}
